/**
 * Canonical guest reservation state resolver.
 *
 * One projection for the whole guest experience: the reservation's lifecycle,
 * and each requirement's own state alongside it. A confirmed reservation stays
 * confirmed while money, verification, a signature or a deposit are still
 * outstanding — those are requirements, not an unfinished checkout — so no
 * caller derives lifecycle from the route it happens to be rendering.
 *
 * The backend arrival-readiness read is the only source of requirement state.
 * When it is unavailable the resolver reports requirements as unavailable and
 * emits no calls to action rather than recomputing them from receipt totals:
 * a remaining balance alone cannot tell a payment due today from one scheduled
 * for next month, and guessing produces exactly the "Confirm and Pay" prompt on
 * a confirmed reservation this replaces.
 */

const LIFECYCLE = {
  requestPending: 'request_pending',
  confirmed: 'confirmed',
  inProgress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
  declined: 'declined',
  expired: 'expired',
  unknown: 'unknown'
};

const STATUS = {
  actionRequired: 'action_required',
  dueToday: 'due_today',
  dueLater: 'due_later',
  scheduled: 'scheduled',
  processing: 'processing',
  complete: 'complete',
  failed: 'failed',
  notRequired: 'not_required',
  unavailable: 'unavailable',
  contactManager: 'contact_manager'
};

const REQUIREMENT = {
  payment: 'payment',
  verification: 'verification',
  signature: 'signature',
  securityDeposit: 'securityDeposit',
  guestInformation: 'guestInformation',
  checkInInstructions: 'checkInInstructions'
};

const COLLECTION = {
  channel: 'channel',
  automatic: 'direct_automatic',
  manual: 'direct_manual'
};

const DEPOSIT = {
  notRequired: 'not_required',
  authorized: 'authorized',
  failed: 'failed',
  scheduled: 'scheduled',
  actionRequired: 'action_required'
};

const OPEN_LIFECYCLES = [LIFECYCLE.confirmed, LIFECYCLE.inProgress];

// Nothing is coming for a reservation that ended or never happened, so its
// checklist is not worth showing: a completed stay does not need check-in
// details "coming before you arrive", and a declined request does not need to
// be told its rental agreement was signed.
const CLOSED_LIFECYCLES = [
  LIFECYCLE.completed,
  LIFECYCLE.cancelled,
  LIFECYCLE.declined,
  LIFECYCLE.expired
];

// Requirements are ordered so the guest sees what only they can resolve first,
// then what is merely upcoming, then what is already handled. Scheduled
// automatic events sort below every genuine action because they are not one.
const PRIORITY = [
  STATUS.failed,
  STATUS.dueToday,
  STATUS.actionRequired,
  STATUS.dueLater,
  STATUS.processing,
  STATUS.scheduled,
  STATUS.contactManager,
  STATUS.unavailable,
  STATUS.complete,
  STATUS.notRequired
];

const REQUIREMENT_ORDER = [
  REQUIREMENT.payment,
  REQUIREMENT.verification,
  REQUIREMENT.signature,
  REQUIREMENT.securityDeposit,
  REQUIREMENT.guestInformation,
  REQUIREMENT.checkInInstructions
];

const ACTIONABLE_STATUSES = [
  STATUS.failed,
  STATUS.actionRequired,
  STATUS.dueToday
];

const number = value =>
  value === undefined || value === null || value === '' ? null : Number(value);

const positive = value => {
  const parsed = number(value);
  return parsed !== null && !Number.isNaN(parsed) && parsed > 0 ? parsed : null;
};

const text = value => (typeof value === 'string' && value ? value : null);

const requirementByKey = (readiness, key) => {
  const requirements = (readiness && readiness.requirements) || [];
  return (
    requirements.find(
      item => (item.key || item.requirement || item.type) === key
    ) || null
  );
};

// The payment route has no slug-scoped variant.
const paymentRoute = bookingCode => `/my-bookings/payment/${bookingCode}`;

const verificationRoute = (bookingCode, slug) =>
  slug
    ? `/my-bookings/verification/${slug}/${bookingCode}`
    : `/my-bookings/verification/${bookingCode}`;

// The agreement is signed where its terms are shown, which is the contract
// page, not the identity steps. It has no slug-scoped variant.
const contractRoute = bookingCode => `/my-bookings/new-contract/${bookingCode}`;

const receiptLifecycle = receipt => {
  const booking = (receipt && receipt.booking) || null;
  if (!booking) return LIFECYCLE.unknown;
  if (booking.cancelled) {
    if (booking.confirmed) return LIFECYCLE.cancelled;
    return booking.archived ? LIFECYCLE.expired : LIFECYCLE.declined;
  }
  if (!booking.confirmed) return LIFECYCLE.requestPending;
  return LIFECYCLE.confirmed;
};

const resolveLifecycle = (readiness, receipt) => {
  const projected = text(readiness && readiness.lifecycle);
  if (projected && Object.values(LIFECYCLE).includes(projected)) {
    return projected;
  }
  return receiptLifecycle(receipt);
};

/**
 * The payment requirement.
 *
 * ``amount_due_now`` is the only signal for a current obligation: a remaining
 * balance that is not yet due is either scheduled (Direct charges it) or due
 * later (the guest pays it then), and neither is an action today. A
 * channel-collected reservation never gets a Direct payment call to action
 * because Direct is not the merchant of record.
 */
const resolvePayment = (payment, { actionable, bookingCode }) => {
  if (!payment) {
    return { key: REQUIREMENT.payment, status: STATUS.unavailable };
  }

  const dueNow = positive(payment.amount_due_now);
  const remaining = positive(payment.balance_remaining);
  const collection = text(payment.collection_method);
  const dueOn = text(payment.balance_due_on_validated);
  const method = payment.payment_method || null;

  if (!dueNow && !remaining) {
    return { key: REQUIREMENT.payment, status: STATUS.complete };
  }

  if (collection === COLLECTION.channel) {
    return {
      key: REQUIREMENT.payment,
      status: STATUS.notRequired,
      informational: true,
      amount: remaining
    };
  }

  if (!actionable) {
    return {
      key: REQUIREMENT.payment,
      status: STATUS.notRequired,
      amount: remaining
    };
  }

  if (dueNow) {
    return {
      key: REQUIREMENT.payment,
      status: STATUS.dueToday,
      amount: dueNow,
      guestActionRequired: true,
      route: paymentRoute(bookingCode)
    };
  }

  if (collection === COLLECTION.automatic) {
    return {
      key: REQUIREMENT.payment,
      status: STATUS.scheduled,
      amount: remaining,
      scheduledDate: dueOn,
      paymentMethod: method
    };
  }

  if (collection === COLLECTION.manual) {
    return {
      key: REQUIREMENT.payment,
      status: STATUS.dueLater,
      amount: remaining,
      dueDate: dueOn,
      route: paymentRoute(bookingCode)
    };
  }

  // A balance with no known collection method: showing it is safe, asking for
  // it is not.
  return {
    key: REQUIREMENT.payment,
    status: STATUS.contactManager,
    amount: remaining
  };
};

const resolveVerification = (readiness, { actionable, bookingCode, slug }) => {
  const identification = requirementByKey(readiness, 'identification');
  const age = requirementByKey(readiness, 'age');
  const parts = [identification, age].filter(
    part => part && part.required !== false
  );

  if (parts.length === 0) {
    return { key: REQUIREMENT.verification, status: STATUS.notRequired };
  }
  if (parts.every(part => part.completed)) {
    return { key: REQUIREMENT.verification, status: STATUS.complete };
  }
  if (!actionable) {
    return { key: REQUIREMENT.verification, status: STATUS.notRequired };
  }
  return {
    key: REQUIREMENT.verification,
    status: STATUS.actionRequired,
    guestActionRequired: true,
    route:
      text((parts.find(part => !part.completed) || {}).route) ||
      verificationRoute(bookingCode, slug)
  };
};

/** The rental agreement is its own requirement — signing it is not identity
 * verification, and a signed agreement must never be labelled as verified. */
const resolveSignature = (readiness, { actionable, bookingCode, slug }) => {
  const agreement = requirementByKey(readiness, 'agreement');
  if (!agreement || agreement.required === false) {
    return { key: REQUIREMENT.signature, status: STATUS.notRequired };
  }
  if (agreement.completed) {
    return { key: REQUIREMENT.signature, status: STATUS.complete };
  }
  if (!actionable) {
    return { key: REQUIREMENT.signature, status: STATUS.notRequired };
  }
  return {
    key: REQUIREMENT.signature,
    status: STATUS.actionRequired,
    guestActionRequired: true,
    route: text(agreement.route) || contractRoute(bookingCode)
  };
};

/**
 * The security deposit, kept separate from the reservation balance.
 *
 * The backend reports whether Direct's authorization job will take the deposit
 * itself; when it will, the guest is told it is scheduled and gets no manual
 * link, so the same obligation is never both "we'll handle it" and "please do
 * it".
 */
const resolveSecurityDeposit = (deposit, { actionable, bookingCode }) => {
  if (!deposit) {
    return { key: REQUIREMENT.securityDeposit, status: STATUS.unavailable };
  }
  if (!deposit.required && !deposit.collected) {
    return { key: REQUIREMENT.securityDeposit, status: STATUS.notRequired };
  }

  const amount = positive(deposit.amount);
  const method = deposit.payment_method || null;

  switch (text(deposit.status)) {
    case DEPOSIT.authorized:
      return {
        key: REQUIREMENT.securityDeposit,
        status: STATUS.complete,
        amount,
        paymentMethod: method
      };
    case DEPOSIT.failed:
      return {
        key: REQUIREMENT.securityDeposit,
        status: actionable ? STATUS.failed : STATUS.notRequired,
        amount,
        guestActionRequired: actionable,
        route: actionable ? paymentRoute(bookingCode) : null
      };
    case DEPOSIT.scheduled:
      return {
        key: REQUIREMENT.securityDeposit,
        status: actionable ? STATUS.scheduled : STATUS.notRequired,
        amount,
        scheduledDate: text(deposit.scheduled_on),
        paymentMethod: method
      };
    case DEPOSIT.actionRequired:
      return {
        key: REQUIREMENT.securityDeposit,
        status: actionable ? STATUS.actionRequired : STATUS.notRequired,
        amount,
        guestActionRequired: actionable,
        route: actionable ? paymentRoute(bookingCode) : null
      };
    case DEPOSIT.notRequired:
      return { key: REQUIREMENT.securityDeposit, status: STATUS.notRequired };
    default:
      return {
        key: REQUIREMENT.securityDeposit,
        status: STATUS.contactManager,
        amount
      };
  }
};

const resolveGuestInformation = (
  readiness,
  { actionable, bookingCode, slug }
) => {
  const info = requirementByKey(readiness, 'guest_information');
  if (!info || info.required === false) {
    return { key: REQUIREMENT.guestInformation, status: STATUS.notRequired };
  }
  if (info.completed) {
    return { key: REQUIREMENT.guestInformation, status: STATUS.complete };
  }
  return {
    key: REQUIREMENT.guestInformation,
    status: actionable ? STATUS.actionRequired : STATUS.notRequired,
    guestActionRequired: actionable,
    route: actionable
      ? text(info.route) || verificationRoute(bookingCode, slug)
      : null
  };
};

/** Check-in instructions are information the guest receives, never an action. */
const resolveCheckInInstructions = (readiness, { lifecycle }) => {
  if (CLOSED_LIFECYCLES.includes(lifecycle)) {
    return { key: REQUIREMENT.checkInInstructions, status: STATUS.notRequired };
  }
  if (readiness && readiness.check_in_email_sent) {
    return {
      key: REQUIREMENT.checkInInstructions,
      status: STATUS.complete,
      informational: true
    };
  }
  return {
    key: REQUIREMENT.checkInInstructions,
    status: STATUS.dueLater,
    informational: true
  };
};

const unavailableRequirements = () =>
  REQUIREMENT_ORDER.reduce((accumulator, key) => {
    accumulator[key] = { key, status: STATUS.unavailable };
    return accumulator;
  }, {});

const sortRequirements = requirements =>
  requirements.slice().sort((left, right) => {
    const byStatus =
      PRIORITY.indexOf(left.status) - PRIORITY.indexOf(right.status);
    if (byStatus !== 0) return byStatus;
    return (
      REQUIREMENT_ORDER.indexOf(left.key) - REQUIREMENT_ORDER.indexOf(right.key)
    );
  });

/**
 * Project one guest reservation into lifecycle plus per-requirement state.
 *
 * @param {Object} options
 * @param {Object|null} options.readiness  arrival-readiness response, or null
 *   when the read failed or has not resolved yet
 * @param {Object|null} options.receipt    receipt slice, used only for the
 *   lifecycle fallback and never for requirement state
 * @param {string} options.bookingCode
 * @param {string} [options.slug]
 */
const deriveGuestStayState = ({
  readiness = null,
  receipt = null,
  bookingCode = '',
  slug = null
} = {}) => {
  const lifecycle = resolveLifecycle(readiness, receipt);
  const actionable = OPEN_LIFECYCLES.includes(lifecycle);
  const context = { actionable, lifecycle, bookingCode, slug };

  const requirements = readiness
    ? {
        [REQUIREMENT.payment]: resolvePayment(readiness.payment, context),
        [REQUIREMENT.verification]: resolveVerification(readiness, context),
        [REQUIREMENT.signature]: resolveSignature(readiness, context),
        [REQUIREMENT.securityDeposit]: resolveSecurityDeposit(
          readiness.security_deposit,
          context
        ),
        [REQUIREMENT.guestInformation]: resolveGuestInformation(
          readiness,
          context
        ),
        [REQUIREMENT.checkInInstructions]: resolveCheckInInstructions(
          readiness,
          context
        )
      }
    : unavailableRequirements();

  const listed = REQUIREMENT_ORDER.map(key => requirements[key]).filter(
    Boolean
  );
  const closed = CLOSED_LIFECYCLES.includes(lifecycle);
  const informational = closed ? [] : listed.filter(item => item.informational);
  const substantive = listed.filter(item => !item.informational);

  return {
    lifecycle,
    actionable,
    requirements,
    requiredActions: sortRequirements(
      substantive.filter(item => ACTIONABLE_STATUSES.includes(item.status))
    ),
    upcomingItems: sortRequirements(
      substantive.filter(item => item.status === STATUS.dueLater)
    ),
    scheduledItems: sortRequirements(
      substantive.filter(
        item =>
          item.status === STATUS.scheduled || item.status === STATUS.processing
      )
    ),
    completedItems: closed
      ? []
      : sortRequirements(
          substantive.filter(item => item.status === STATUS.complete)
        ),
    unresolvedItems: sortRequirements(
      substantive.filter(
        item =>
          item.status === STATUS.unavailable ||
          item.status === STATUS.contactManager
      )
    ),
    informationalItems: informational,
    // A cancellation's refund position, as recorded rather than as entitled:
    // `outstanding` means money is still held, never that a refund is on its way.
    refund: (readiness && readiness.refund) || null,
    dataWarnings: (readiness && readiness.data_warnings) || []
  };
};

module.exports = {
  LIFECYCLE,
  STATUS,
  REQUIREMENT,
  deriveGuestStayState
};
